import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BypassConfirmModalProps {
    errors: string[];
    resolveError: (code: string) => string;
    onConfirm: (reason: string) => void;
    onCancel: () => void;
}

const MAX_REASON_LENGTH = 100;

const BypassConfirmModal: React.FC<BypassConfirmModalProps> = ({
    errors,
    resolveError,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');

    const trimmedReasonLength = reason.trim().length;
    const reasonLength = reason.length;
    const reasonValid = trimmedReasonLength >= 5 && reasonLength <= MAX_REASON_LENGTH;
    const canContinue = reasonValid;
    const handleReasonChange = (value: string) => {
        setReason(value.length > MAX_REASON_LENGTH ? value.slice(0, MAX_REASON_LENGTH) : value);
    };
    const handleReasonBeforeInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
        const inputEvent = event.nativeEvent as InputEvent;
        if (inputEvent.inputType?.startsWith('delete')) return;
        if (!inputEvent.data) return;

        const { selectionStart, selectionEnd } = event.currentTarget;
        const selectedLength = selectionEnd - selectionStart;
        const nextLength = reasonLength - selectedLength + inputEvent.data.length;

        if (nextLength > MAX_REASON_LENGTH) {
            event.preventDefault();
        }
    };
    const handleReasonPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const pastedText = event.clipboardData.getData('text');
        if (!pastedText) return;

        const { selectionStart, selectionEnd } = event.currentTarget;
        const selectedLength = selectionEnd - selectionStart;
        const availableLength = MAX_REASON_LENGTH - (reasonLength - selectedLength);

        if (availableLength < pastedText.length) {
            event.preventDefault();
            if (availableLength <= 0) return;

            const nextReason = [
                reason.slice(0, selectionStart),
                pastedText.slice(0, availableLength),
                reason.slice(selectionEnd),
            ].join('');

            setReason(nextReason);
        }
    };

    return (
        <div className="pm-bypass-overlay">
            <div className="pm-bypass-modal">
                <div className="cco_form_message cco_form_message_error pm-selfie-review__error pm-bypass-modal__error">
                    <span className="pm-beta-tag">BETA</span>
                    <ul>
                        {errors.map((code, i) => (
                            <li key={i}>{resolveError(code)}</li>
                        ))}
                    </ul>
                    <p className="pm-ai-disclaimer">{t('PhotoManagement.errors.aiDisclaimer')}</p>
                </div>

                <label className="pm-bypass-modal__reason-label">
                    {t('PhotoManagement.bypass.reasonLabel')}
                </label>
                <textarea
                    className="pm-bypass-modal__reason-input"
                    placeholder={t('PhotoManagement.bypass.reasonPlaceholder')}
                    value={reason}
                    onBeforeInput={handleReasonBeforeInput}
                    onChange={e => handleReasonChange(e.target.value)}
                    onPaste={handleReasonPaste}
                    maxLength={MAX_REASON_LENGTH}
                    rows={3}
                />
                <div className={`pm-bypass-modal__char-count${reasonLength === MAX_REASON_LENGTH ? ' pm-bypass-modal__char-count--max' : ''}`}>
                    {reasonLength}/{MAX_REASON_LENGTH}
                    {reasonLength > 0 && trimmedReasonLength < 5 && (
                        <span className="pm-bypass-modal__char-hint"> (min 5)</span>
                    )}
                </div>

                <div className="pm-bypass-modal__actions">
                    <button
                        className="pm-btn-primary"
                        onClick={onCancel}
                    >
                        {t('PhotoManagement.bypass.backButton')}
                    </button>
                    <button
                        className="pm-btn-secondary"
                        disabled={!canContinue}
                        onClick={() => canContinue && onConfirm(reason.trim())}
                    >
                        {t('PhotoManagement.bypass.continueButton')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BypassConfirmModal;
